import { useEffect, useRef } from "react"
import { toast } from "react-toastify/unstyled";

import {
  Status,
  SubscribeImportStatusResponse,
} from "~/server/gen_client/unacast/boundaries/api/v1/api_pb"
import { useBoundaryState, useBoundaryActions } from "~/contexts/BoundaryStatusContext"
import { useRevalidator } from "react-router"

export function useStatusToasts() {
  const { messages, listeningIds } = useBoundaryState()
  const { cleanupMessages, removeListeningId } = useBoundaryActions()
  const processedMessages = useRef<Record<string, string>>({})
  const toastIds = useRef<string[]>([])
  const revalidator = useRevalidator()
  
  useEffect(() => {
    listeningIds.forEach((id) => {
      ;(messages[id] || []).forEach((message) => {
        try {
          const parsedMessage =
            SubscribeImportStatusResponse.fromJsonString(message)
          const { requestId, status } = parsedMessage
          if (!requestId) return
          processedMessages.current[requestId] = message
          if (!toastIds.current.includes(requestId)) {
            toastIds.current.push(requestId)
          }

          const statusString = Status[status]
          let description = ""
          let variant: "success" | "error" | "default" = "default"
          let isFinalState = false
          switch (status) {
            case Status.PRELIMINARY_VALIDATION_SUCCESS:
              description = "File validated successfully..."
              isFinalState = false
              variant = "default"
              break
            case Status.PRELIMINARY_VALIDATION_FAILED:
              description = `File validation failed: ${JSON.stringify(
                parsedMessage.data.value,
              )}`
              variant = "error"
              isFinalState = true
              break
            case Status.BOUNDARIES_CREATED:
              description = "Boundaries are being created..."
              isFinalState = false
              variant = "default"
              break
            case Status.SUCCESS:
              description = "Boundaries created successfully"
              variant = "success"
              isFinalState = true
              break
            case Status.INTERNAL_FAILURE:
              description = "Internal failure"
              variant = "error"
              isFinalState = true
              break
            case Status.REJECTED:
              description = "Rejected"
              variant = "error"
              isFinalState = true
              break
          }
          if (toast.isActive(requestId)) {
            toast.update(requestId, {
              render: (
                <div>
                  <div className="font-bold flex flex-row gap-2">
                    {statusString.toLowerCase()}
                  </div>
                  <div className="flex flex-col gap-2 w-full">
                    <div className="flex flex-row gap-2 items-center">
                      <span>{description}</span>
                    </div>
                  </div>
                </div>
              ),
              type: variant,
              isLoading: isFinalState ? false : true,
              autoClose: isFinalState ? 5000 : false,
              closeOnClick: isFinalState ? true : false,
              draggable: isFinalState ? true : false,
            })

            if (isFinalState) {
              removeListeningId(requestId)
              cleanupMessages(requestId)
              revalidator.revalidate()
            }
          } else {
            // Create a new loading toast
            toast.loading(description, {
              toastId: requestId,
              isLoading: true,
              autoClose: false,
              closeOnClick: false,
              draggable: false,
            })
          }
        } catch (error) {
          console.error("Failed to parse status message:", error)
          toast.error(message)
        }
      })
    })
  }, [messages, listeningIds, cleanupMessages, removeListeningId, revalidator])
}
